import React, { useState, useEffect } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useHistory } from 'react-router-dom';
import { calculateAge, titleCase, padZero, calculateTimeDiff, debugLogToConsole } from '../../utils';
import { gray, blue } from '../../theme';

const GanttCaseCell = (props) => {
  const [left, setLeft] = useState(props.left);
  const [position, setPosition] = useState(props.position);
  const [width, setWidth] = useState(props.width);
  const [patientString, setPatientString] = useState('N/A');
  const [surgStr, setSurgStr] = useState('');
  const [caseTimesStr, setCaseTimesStr] = useState('');
  const [procString, setProcString] = useState('');
  const [anesString, setAnesString] = useState('');
  const [crnaString, setCrnaString] = useState('');

  const history = useHistory();

  useEffect(() => {
    const {
      left,
      width,
      position,
      surgStr,
      cbCasesData,
      anesStr,
      crnaStr
    } = props

    const {
      surgicalProcedures = [],
      otherSurgicalProcedures = [],
      blockEndHour,
      blockEndMinute,
      blockStartHour,
      blockStartMinute
    } = cbCasesData;

    // Left, Position, and Width
    setLeft(left);
    setPosition(position);
    setWidth(width);

    // Patient String
    if (cbCasesData.patientData) {
      const {
        firstName = '',
        lastName = '',
        gender = '',
        dob = ''
      } = cbCasesData.patientData;
      let pAge = '';
      if (dob.length > 0) {
        const tmpDob = dob.split('T');
        const tmp = tmpDob[0].split('-');
        pAge = calculateAge(`${tmp[1]}/${tmp[2]}/${tmp[0]}`);
      }
      const ptGender = (gender.length > 0 && gender.toLowerCase() === 'male') ? 'M' : 'F';
      setPatientString(`${titleCase(lastName)}, ${titleCase(firstName)} (${pAge} ${ptGender})`);
    }

    // Surgeon String
    setSurgStr(surgStr);

    // Anes String
    setAnesString(anesStr);

    // CRNA String
    setCrnaString(crnaStr);

    // Procedures String
    // let tmpProcStringParts = '';
    let tmpProcStringParts = '';
    if (surgicalProcedures.length > 0) {
      tmpProcStringParts = surgicalProcedures[0].description.split['//'];
      // surgicalProcedures.forEach((proc, idx) => {
      //   tmpProcStringParts += `${titleCase(proc.description)}${idx < surgicalProcedures.length - 1 ? ' | ' : ''}`;
      // });
    }

    if (otherSurgicalProcedures.length > 0) {
      tmpProcStringParts = otherSurgicalProcedures[0].description.split('//');
    }
    if (tmpProcStringParts && Array.isArray(tmpProcStringParts) && tmpProcStringParts.length > 0) {
      setProcString(tmpProcStringParts[0]);
    }

    // Case times string
    const caseDuration = calculateTimeDiff(`${blockStartHour}:${blockStartMinute}`, `${blockEndHour}:${blockEndMinute}`);
    setCaseTimesStr(`${padZero(blockStartHour)}${padZero(blockStartMinute)} - ${padZero(blockEndHour)}${padZero(blockEndMinute)} (${caseDuration} mins)`);
  }, [props]);

  const styles = StyleSheet.create({
    ganttCell: {
      position: 'absolute',
      left: `${left}px`,
      top: position > 0 ? `${position * (props.height - 2)}px` : '0',
      width: `${width}px`,
      background: 'white',
      color: 'black',
      height: `${props.height - 2}px`,
      fontSize: '12px',
      padding: '0 4px',
      lineHeight: '16px',
      borderRight: `1px solid ${gray[900]}`,
      borderBottom: `1px solid ${gray[900]}`
    },
    cellRow: {
      height: '20px',
      marginBottom: '1px',
      display: 'flex',
      alignItems: 'center'
    },
    topCellRow: {
      height: '25px',
      marginBottom: '1px',
      display: 'flex',
      alignItems: 'center'
    },
    cellRowIcon: {
      background: gray[400],
      borderRadius: '2px',
      width: '20px',
      height: '20px',
      display: props.width < 160 ? 'none' : ''
    },
    topHeading: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'space-between',
      fontWeight: '700',
      color: blue[800],
      paddingRight: '2px',
      paddingTop: '2px',
      width: '100%'
    },
    topHeadingTextLeft: {
      flexGrow: '1',
      lineHeight: '18px'
    },
    topHeadingExpandBtn: {
      position: 'absolute',
      fill: blue[700],
      right: '0',
      ':hover': {
        fill: blue[500]
      }
    },
    cellRowText: {
      color: 'black',
      marginLeft: '4px'
    }
  });

  return (
    <div className={css(styles.ganttCell)}>
      {/* Patient Name, DOB etc */}
      <div className={`fx ${css(styles.topCellRow)}`}>
        <div className={`text-truncate ${css(styles.topHeading)}`}>
          {/* Text */}
          <div className={css(styles.topHeadingTextLeft)}>
            {patientString}
          </div>
          {/* Expand Icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            className={`ml-2 ${css(styles.topHeadingExpandBtn)}`}
            onClick={() => {
              const readOnlyMode = false;
              localStorage.setItem("loadGanttGrid", JSON.stringify(true));
              history.push(`/schedule/${props.id}/${readOnlyMode}`);
            }}
          >
            <path
              fill="inherit"
              fillRule="evenodd"
              d="M2 0a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2Zm12.508 5h-3.587a.491.491 0 1 0 0 .982h2.36l-2.594 2.59a.49.49 0 1 0 .695.694l2.633-2.63v2.337a.491.491 0 0 0 .984 0V5.49a.49.49 0 0 0-.49-.49ZM9.08 14.01H6.72l2.593-2.59a.49.49 0 1 0-.695-.695l-2.635 2.63v-2.337a.491.491 0 0 0-.983 0v3.483c0 .271.22.491.492.491h3.587a.491.491 0 0 0 0-.982Z"
              clipRule="evenodd"
            />
          </svg>
          <div></div>
        </div>
      </div>

      {/* Surgeon */}
      <div className={`fx ${css(styles.cellRow)}`}>
        {/* Icon */}
        {/* <div className={`fx fx__hv-center ${css(styles.cellRowIcon)}`} /> */}
        {/* Text */}
        <div className={`text-truncate ${css(styles.cellRowText)}`}>
          {surgStr}
        </div>
      </div>

      {/* Surgical Procedures */}
      <div className={`fx ${css(styles.cellRow)}`}>
        {/* Icon */}
        {/* <div className={`fx fx__hv-center ${css(styles.cellRowIcon)}`} /> */}
        {/* Text */}
        <div className={`text-truncate ${css(styles.cellRowText)}`}>
          {procString}
        </div>
      </div>

      {/* Anesthesiologist / CRNA */}
      <div className={`fx ${css(styles.cellRow)}`}>
        {/* Icon */}
        {/* <div className={`fx fx__hv-center ${css(styles.cellRowIcon)}`} /> */}
        {/* Text */}
        <div className={`text-truncate ${css(styles.cellRowText)}`}>
          {`${anesString} | ${crnaString}`}
        </div>
      </div>

      {/* Case times */}
      {/* <div className={`fx ml-4 ${css(styles.cellRow)}`} >
        <div className={`text-truncate ${css(styles.patientCardFieldValue)}`}>
          {caseTimesStr}
        </div>
      </div> */}
    </div>
  );
};

GanttCaseCell.defaultProps = {
  height: 56
};
export default GanttCaseCell;
